import type { ButtonHTMLAttributes } from 'react';

export interface DisclosureChevronProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> {
  /** Whether the disclosure is currently expanded. Controls the rotation. */
  expanded: boolean;
  /** Required for icon-only buttons. */
  'aria-label': string;
}

/**
 * Row-leading disclosure affordance — a 16px button rendering a `▸` glyph
 * that rotates 90° when `expanded`. Distinct from `<Disclosure>` (which owns
 * the entire summary+body shell) and `<IconButton>` (which is square and
 * sized for header/card actions). Caller owns the open state and the rotated
 * region; this primitive is the click target + rotation only.
 *
 * Used by the tasks list to mark parent rows that contain children. CSS
 * namespace `pn-disclosure-chevron`.
 */
export function DisclosureChevron({
  expanded,
  className,
  type,
  ...rest
}: DisclosureChevronProps) {
  const cls = ['pn-disclosure-chevron', className].filter(Boolean).join(' ');
  return (
    <button
      type={type ?? 'button'}
      className={cls}
      data-expanded={expanded ? '1' : '0'}
      aria-expanded={expanded}
      {...rest}
    >
      <span className="pn-disclosure-chevron-glyph" aria-hidden>
        ▸
      </span>
    </button>
  );
}
